import SeoLandingLayout from "@/components/cre/SeoLandingLayout";

const RELATED = [
  { to: "/aiops-incident-reasoning", label: "AIOps incident reasoning" },
  { to: "/root-cause-analysis-ai", label: "Root cause analysis AI" },
  { to: "/major-incident-management", label: "Major incident management" },
  { to: "/it-downtime-reduction", label: "IT downtime reduction" },
];

const FAQ = [
  {
    q: "What is AI incident management?",
    a: "AI incident management uses domain-aware reasoning agents to help on-call engineers, incident managers, and SRE teams interpret incident signals faster. CREAIOps turns fragmented symptoms into structured hypotheses, ranked next steps, and human-reviewable runbooks — without taking automated action.",
  },
  {
    q: "Does CREAIOps replace ITSM tools like ServiceNow or Jira?",
    a: "No. CREAIOps complements existing ITSM and observability platforms as an AI decision-intelligence layer. Tickets and on-call workflows continue to live in your system of record; CREAIOps adds the reasoning, RCA hypotheses, and approval-ready runbooks on top.",
  },
  {
    q: "Will CREAIOps auto-fix or auto-remediate production issues?",
    a: "No. CREAIOps is advisory only. Every recommended action requires explicit human approval and is recorded in an audit trail. CREAIOps does not push changes or run scripts on your infrastructure.",
  },
  {
    q: "What data should I share with CREAIOps?",
    a: "Use anonymized incident inputs only. Do not share passwords, secrets, customer data, regulated data, or sensitive production logs. Inputs are processed to generate AI-assisted reasoning outputs.",
  },
  {
    q: "Can I try CREAIOps without signing up?",
    a: "Yes. The no-login preview lets you run a single anonymized incident through CREAIOps and see RCA hypotheses, recommended actions, and a stakeholder draft instantly.",
  },
];

export default function AIIncidentManagement() {
  return (
    <SeoLandingLayout
      slug="ai-incident-management"
      metaTitle="AI Incident Management Software | CREAIOps"
      metaDescription="CREAIOps is AI incident management for ITSM, SRE, and IT operations teams. Turn fragmented incident signals into ranked RCA hypotheses, recommended actions, and human-approved runbooks — without auto-remediation."
      h1="AI incident management for modern IT operations teams"
      intro="CREAIOps brings AI decision intelligence to incident management. Multi-agent reasoning across application, infrastructure, network, security, database, and business-impact dimensions helps on-call engineers and incident managers reach the right conclusion faster — with human approval at every step."
      problem={{
        title:
          "Incident response is slowed by fragmented signals, noisy bridges, and tribal knowledge",
        body:
          "Modern IT environments generate alerts faster than humans can correlate them. On-call engineers piece together logs, metrics, and chat threads under time pressure, while leadership asks for impact and ETA on the same call. Tickets capture what happened, but rarely accelerate the reasoning needed to resolve a major incident.",
        points: [
          "Cross-team coordination is dominated by status updates, not analysis",
          "RCA arrives long after the incident is closed, not when it could change the outcome",
          "Senior engineers act as the unofficial reasoning engine — and burn out",
          "Stakeholder updates are written from scratch each time, even for similar incidents",
        ],
      }}
      howWeHelp={{
        title:
          "An AI reasoning layer that sits beside your ITSM and observability stack",
        body:
          "CREAIOps dispatches specialist reasoning agents per incident. Each agent publishes hypotheses, supporting evidence, and confidence, and CREAIOps combines them into a single ranked view. Engineers debate signals, not opinions — and walk away with an approval-ready runbook draft.",
        items: [
          {
            title: "Multi-agent RCA hypotheses",
            desc: "Domain agents for application, infrastructure, network, database, security, and service impact propose likely causes ranked by weighted consensus.",
          },
          {
            title: "Ranked, safe next actions",
            desc: "Prioritised actions with blast radius and rollback context — designed to be reviewed by a human before execution.",
          },
          {
            title: "Stakeholder-ready updates",
            desc: "One concise narrative for leadership and engineers, generated from the same reasoning trail.",
          },
          {
            title: "Audit-ready governance",
            desc: "Every approval, override, and decision is logged with the reasoning context that informed it.",
          },
        ],
      }}
      benefits={[
        "Faster time-to-hypothesis on major incidents",
        "Less reliance on a single senior engineer to drive every bridge",
        "Cleaner stakeholder communication during high-pressure outages",
        "Auditable record of what was considered, decided, and approved",
        "Consistent reasoning quality across shifts, teams, and regions",
        "Lower mean time to recovery without compromising change safety",
      ]}
      useCase={{
        title:
          "A regional bank uses CREAIOps to coordinate a payments outage",
        body:
          "When a payments incident is raised, CREAIOps dispatches agents across application, infrastructure, network, and database lenses. Within minutes the incident manager sees ranked hypotheses, a customer-facing draft, and a human-reviewable runbook — ready for approval by the change advisory team.",
        steps: [
          "Incident manager submits an anonymized incident brief into CREAIOps",
          "Domain agents publish hypotheses with confidence and supporting reasoning",
          "Consensus engine ranks likely root causes and surfaces safe next actions",
          "Stakeholder update draft is generated and tuned for leadership",
          "Runbook draft is reviewed and approved by an authorised human reviewer",
          "Audit transcript is exported for problem management follow-up",
        ],
      }}
      faq={FAQ}
      related={RELATED}
    />
  );
}
